// NOTE required to be child of ApolloProvider component AND react router
import React, { FC } from 'react'

import { Card, Skeleton, Statistic, Typography } from 'antd'
import { useQuery } from '@apollo/client'

export interface KPICardProps {
  value: string | number
  title: string
  subTitle?: string
  href?: string
  linkText?: string
  style?: React.CSSProperties
  accent?: string
}

export const KPICard: FC<KPICardProps> = (props) => {
  const { href, title, subTitle, value, style, linkText, accent = '#6B2EF0', children } = props

  return (
    <Card
      loading={!value && value !== 0}
      style={{
        background: '#F5F5F5',
        borderColor: '#CFCFCF',
        color: accent,
        borderRadius: 5,
        flex: '1 0',
        ...style,
      }}
      bodyStyle={{
        display: 'flex',
        flexFlow: 'column nowrap',
        padding: '14px 18px',
        height: '100%',
      }}
    >
      <span
        style={{
          fontSize: 22,
          lineHeight: 1,
          fontWeight: 600,
        }}
      >
        {title}
      </span>
      <div
        style={{
          marginTop: children ? 12 : 0,
          flexGrow: 1,
          alignSelf: 'stretch',
          height: children ? 52 : 0,
        }}
      >
        {children ? children : ''}
      </div>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'flex-end',
          fontSize: 40,
          lineHeight: 1,
          fontWeight: 600,
          height: 40,
          marginTop: 15,
          marginBottom: 3,
        }}
      >
        {(+value).toLocaleString()}
      </span>
      <footer
        style={{
          display: 'flex',
          flexFlow: 'row wrap',
        }}
      >
        <span
          style={{
            color: '#6649A6',
            textTransform: 'uppercase',
            fontSize: 16,
            lineHeight: 1,
            flex: '1 0',
            marginRight: 8,
          }}
        >
          {subTitle}
        </span>
        {linkText && href && (
          <a
            href={href}
            style={{
              color: accent,
              textTransform: 'uppercase',
              fontSize: 16,
              fontWeight: 600,
              lineHeight: 1,
              flex: '0 1',
              whiteSpace: 'nowrap',
            }}
          >
            {linkText} &rarr;
          </a>
        )}
      </footer>
    </Card>
  )
}

export interface StatProps {
  href?: string
  parseQueryResult: (data: { [key: string]: any }) => string | number
  query: any
  title: string
  subTitle?: string
  variables: { [key: string]: any }
  style?: React.CSSProperties
  realtimeValue?: any
}

export const Stat: FC<StatProps> = ({
  href,
  parseQueryResult,
  query,
  title,
  variables,
  subTitle,
  style,
  realtimeValue,
}) => {
  const { data, loading, error } = useQuery(query, {
    fetchPolicy: 'no-cache', // ensure refetch on componentDidMount
    variables,
  })

  if (error) {
    console.error(`error fetching stat (${title})`, error)
  }

  return (
    <KPICard
      // TODO: apply this style via parent style (> &:not(:last-child)) when we have better CSS workflow
      style={{ ...style }}
      title={title}
      subTitle={subTitle}
      value={realtimeValue || (loading || error ? null : parseQueryResult(data))}
      href={href}
    />
  )
}
